import React, { useEffect, useState } from 'react';

import apiClient from '../../../services/api';
import { HashtagPreset, MaybePaginated } from '../../../types/api';

interface ComposeStepProps {
  description: string;
  onDescriptionChange: (next: string) => void;
  /** Local "YYYY-MM-DDTHH:MM" string — same on-the-wire shape the
   *  wizard already persists. Empty = post immediately. The UI splits
   *  it into separate date + time inputs but writes back this format
   *  so wizardState.WizardDraft + the publish-time conversion stay
   *  unchanged. */
  scheduleFor: string;
  onScheduleChange: (next: string) => void;
}

/* Split scheduleFor's "YYYY-MM-DDTHH:MM" into date + time halves for
 * the two inputs. Returns sensible defaults when the input is empty
 * so the user can tweak either side first. */
function splitSchedule(s: string): { date: string; time: string } {
  if (!s) return { date: '', time: '' };
  const [date, time] = s.split('T');
  return { date: date || '', time: time || '' };
}

function joinSchedule(date: string, time: string): string {
  if (!date) return '';
  return `${date}T${time || '12:00'}`;
}

export default function ComposeStep({
  description, onDescriptionChange,
  scheduleFor, onScheduleChange,
}: ComposeStepProps) {
  const [presets, setPresets] = useState<HashtagPreset[]>([]);

  useEffect(() => {
    apiClient.get<MaybePaginated<HashtagPreset>>('/posts/hashtag-presets/')
      .then((data) => {
        setPresets(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => { /* presets are optional */ });
  }, []);

  function applyPreset(p: HashtagPreset): void {
    const trimmed = description.trimEnd();
    /* Always insert a blank line between the existing caption text and
     * the preset's hashtags so they're visually grouped at the bottom
     * of the post. */
    const sep = trimmed ? '\n\n' : '';
    onDescriptionChange(`${trimmed}${sep}${p.hashtags.trim()}`);
  }

  const { date, time } = splitSchedule(scheduleFor);

  return (
    <div className="wizard-compose">
      <div className="wizard-compose-field">
        <div className="wizard-compose-caption-head">
          <span className="section-label">What's the caption?</span>
        </div>
        <textarea
          rows={6}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="What do you want to post?"
          autoFocus
        />
        {/* Hashtag presets, always visible — tap to append a preset to
            the caption (blank-line separated). Managed on /hashtags. */}
        {presets.length > 0 && (
          <div className="wizard-compose-picker">
            <span className="muted">Hashtags:</span>
            <ul className="preset-row">
              {presets.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="secondary btn--sm"
                    onClick={() => applyPreset(p)}
                  >
                    + {p.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="wizard-compose-field">
        <span className="section-label">Schedule</span>
        <div className="wizard-compose-schedule">
          <label className="wizard-compose-sched-cell">
            <span className="muted">Date</span>
            <input
              type="date"
              className="schedule-input"
              value={date}
              onChange={(e) => onScheduleChange(joinSchedule(e.target.value, time))}
            />
          </label>
          <label className="wizard-compose-sched-cell">
            <span className="muted">Time</span>
            <input
              type="time"
              className="schedule-input"
              value={time}
              onChange={(e) => onScheduleChange(joinSchedule(date, e.target.value))}
              disabled={!date}
            />
          </label>
          {scheduleFor && (
            <button
              type="button"
              className="ghost btn--sm wizard-compose-sched-clear"
              onClick={() => onScheduleChange('')}
            >
              Post immediately
            </button>
          )}
        </div>
        <small className="muted">
          {scheduleFor
            ? `Will be posted on ${new Date(scheduleFor).toLocaleString()}.`
            : 'Leave blank to post the moment you tap Post.'}
        </small>
      </div>
    </div>
  );
}
